import React, { useState, useEffect } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { doc, getDoc, setDoc, Timestamp } from "firebase/firestore";
import { db } from "services/firebaseConfig";
import { Button } from "components/ui/button";
import { Checkbox } from "components/ui/checkbox";
import toast from "react-hot-toast";
import { isSameDay, getMonth, isWeekend } from "date-fns";
import { checkHoliday } from "services/holidayService";
import { isParkOpen } from "utils/calendarRules";

const MESES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const AdminCalendarManager = () => {
  const [mesesFechamento, setMesesFechamento] = useState([]);
  const [excecoes, setExcecoes] = useState([]); // [{ date: Date, status: 'aberto'|'fechado' }]
  const [loading, setLoading] = useState(true);

  // Carrega configuração inicial
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const docRef = doc(db, "config", "calendario");
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          const data = docSnap.data();
          setMesesFechamento(data.mesesFechamento || []);
          // Converte Timestamps para Dates
          const loadedExcecoes = (data.excecoes || []).map((ex) => ({
            ...ex,
            date: ex.date.toDate ? ex.date.toDate() : new Date(ex.date),
          }));
          setExcecoes(loadedExcecoes);
        }
      } catch (error) {
        console.error("Erro ao carregar calendário:", error);
        toast.error("Erro ao carregar configurações do calendário.");
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  // Salva no Firestore
  const handleSave = async () => {
    try {
      const docRef = doc(db, "config", "calendario");
      await setDoc(docRef, {
        mesesFechamento,
        excecoes: excecoes.map((ex) => ({
          ...ex,
          date: Timestamp.fromDate(ex.date),
        })),
      });
      toast.success("Regras do calendário atualizadas!");
    } catch (error) {
      console.error("Erro ao salvar:", error);
      toast.error("Erro ao salvar regras.");
    }
  };

  const toggleMes = (index) => {
    setMesesFechamento((prev) => {
      if (prev.includes(index)) {
        return prev.filter((i) => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const handleDayClick = (date) => {
    // 1. Verifica estado ATUAL (sem exceção manual) para saber qual seria o padrão
    const isSazonalFechado = mesesFechamento.includes(getMonth(date));
    const isFimDeSemana = isWeekend(date);
    const isFeriado = !!checkHoliday(date);

    // Se Sazonal Fechado -> Padrão é FECHADO.
    // Se não, segue Fim de Semana ou Feriado -> ABERTO. Caso contrário -> FECHADO.
    let padraoAberto = false;
    if (!isSazonalFechado) {
      if (isFimDeSemana || isFeriado) padraoAberto = true;
    }

    // 2. Verifica se já existe exceção para este dia
    const indexExcecao = excecoes.findIndex((ex) => isSameDay(ex.date, date));

    if (indexExcecao >= 0) {
      // Se já tem exceção, removemos (volta ao padrão)
      const novasExcecoes = [...excecoes];
      novasExcecoes.splice(indexExcecao, 1);
      setExcecoes(novasExcecoes);
      toast("Exceção removida. Data segue regra padrão.");
    } else {
      // Se não tem exceção, criamos uma INVERTENDO o padrão
      const novoStatus = padraoAberto ? "fechado" : "aberto";
      setExcecoes([...excecoes, { date, status: novoStatus }]);
      toast.success(
        `Exceção criada: Dia definido como ${novoStatus.toUpperCase()}`,
      );
    }
  };

  // Função para estilizar os dias no calendário
  const tileClassName = ({ date, view }) => {
    if (view !== "month") return null;

    // Verifica estado final usando a função utilitária
    const isOpen = isParkOpen(date, excecoes, mesesFechamento);

    // Verifica se é exceção manual
    const isException = excecoes.some((ex) => isSameDay(ex.date, date));

    // Classes bases
    let classes = "border rounded-sm relative ";

    // Cores de fundo (Estado Final) - Usando !important (prefixo !) para sobrescrever default e dark mode
    // Cores de fundo (Estado Final)
    if (isOpen) {
      // Aberto:
      // Light: Verde Claro (#dcfce7) texto Escuro (#14532d)
      // Dark: Verde Escuro (#14532d) texto Claro (#dcfce7)
      classes +=
        "!bg-green-100 !text-green-900 dark:!bg-green-900 dark:!text-green-100 hover:!bg-green-200 dark:hover:!bg-green-800 ";
    } else {
      // Fechado:
      // Light: Cinza Claro (#f3f4f6) texto Cinza (#6b7280)
      // Dark: Slate Escuro (#1e293b) texto Slate (#94a3b8)
      classes +=
        "!bg-gray-100 !text-gray-500 dark:!bg-slate-800 dark:!text-slate-400 hover:!bg-gray-200 dark:hover:!bg-slate-700 ";
    }

    // Bordas (Indicador de Exceção)
    if (isException) {
      classes += isOpen
        ? "!border-orange-500 border-2 "
        : "!border-red-500 border-2 ";
    } else {
      classes += "border-transparent ";
    }

    return classes;
  };

  if (loading) return <div className="p-4">Carregando calendário...</div>;

  return (
    <div className="space-y-8 p-4 bg-white rounded-lg shadow dark:bg-slate-900">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Gerenciador de Calendário</h2>
        <Button onClick={handleSave}>Salvar Alterações</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Lado Esquerdo: Checkboxes Sazonais */}
        <div className="space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">
            Fechamento Sazonal (Meses Inteiros)
          </h3>
          <p className="text-sm text-muted-foreground">
            Marque os meses em que o parque estará <strong>FECHADO</strong>.
            Exceções manuais ainda podem abrir datas específicas nestes meses.
          </p>
          <div className="grid grid-cols-2 gap-3">
            {MESES.map((mes, index) => (
              <div key={mes} className="flex items-center space-x-2">
                <Checkbox
                  id={`mes-${index}`}
                  checked={mesesFechamento.includes(index)}
                  onCheckedChange={() => toggleMes(index)}
                />
                <label
                  htmlFor={`mes-${index}`}
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  {mes}
                </label>
              </div>
            ))}
          </div>
        </div>

        {/* Lado Direito: Calendário Interativo */}
        <div className="lg:col-span-2 space-y-4">
          <h3 className="font-semibold text-lg border-b pb-2">
            Calendário de Exceções
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Clique em um dia para criar ou remover uma exceção.
            <br />
            <span className="inline-block w-3 h-3 bg-green-100 border border-transparent mr-1"></span>
            Aberto (Padrão)
          </p>

          <div className="flex justify-center admin-calendar-wrapper whitespace-nowrap">
            <style>{`
              .dark .admin-calendar-wrapper .react-calendar {
                background: #1e293b !important;
                border: 1px solid #334155 !important;
                color: #f8fafc !important;
                font-family: inherit;
              }
              .dark .admin-calendar-wrapper .react-calendar__navigation {
                background: transparent !important;
                display: flex;
              }
              .dark .admin-calendar-wrapper .react-calendar__navigation button {
                color: #f8fafc !important;
                background: transparent !important;
                min-width: 44px;
              }
              .dark .admin-calendar-wrapper .react-calendar__navigation button:disabled {
                background: transparent !important;
              }
              .dark .admin-calendar-wrapper .react-calendar__navigation button:enabled:hover,
              .dark .admin-calendar-wrapper .react-calendar__navigation button:enabled:focus {
                background: #334155 !important;
                border-radius: 6px;
              }
              .dark .admin-calendar-wrapper .react-calendar__month-view__weekdays__weekday {
                color: #94a3b8 !important; 
                text-decoration: none !important;
              }
               .dark .admin-calendar-wrapper .react-calendar__month-view__weekdays__weekday abbr {
                text-decoration: none !important;
              }
            `}</style>
            <Calendar
              onChange={() => {}}
              value={null}
              onClickDay={handleDayClick}
              tileClassName={tileClassName}
              locale="pt-BR"
              minDetail="month"
              showNeighboringMonth={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCalendarManager;
